/**
 * The single source of truth for turning an API failure into something a user can read.
 *
 * Before TASK-4738 this logic existed in eight places: three parallel translators
 * (`translateRouteError`, `translatePlanningError`, `extractBuildError`), four private
 * `extractErrorMessage` copies in the admin settings panels, and ~15 hand-rolled `setError`
 * loops in the admin modals. Each handled a different subset of the error shapes the backend
 * actually produces, so which failures got a real message and which collapsed to "An error
 * occurred" depended on nothing but which file the call happened to live in.
 *
 * ## Resolution order
 *
 * 1. **`code`** — the stable machine-readable identifier from TASK-4737. Preferred over every
 *    other branch because it is a contract: the backend may reword `detail` freely, and a code
 *    survives that. See {@link ERROR_CODE_MAP}.
 * 2. **`detail`** — the conventional DRF single-message shape (DEV_RULES §6.4). Passed through
 *    the prose maps first (see below), then returned verbatim.
 * 3. **DRF field errors** — `{"field": ["msg"]}`. Returned as a flattened message *and* as a
 *    `fieldErrors` map so a form can put each message next to its own input.
 * 4. **`non_field_errors`** — the cross-field shape. Surfaced at form level.
 * 5. **A localized generic fallback** — `common.errorOccurred`, reached only when the server
 *    genuinely said nothing usable.
 *
 * ## Why the English-prose maps still exist
 *
 * `code` is **not** universally present, and treating a missing code as anomalous would be a
 * bug. Roughly 207 sites platform-wide answer with a bare `return Response({...}, status=4xx)`,
 * which never passes through `EXCEPTION_HANDLER` and therefore carries no code at all.
 * TASK-4737 added `code` to the in-scope flows only. Verified examples of the split:
 *
 * - All ten route-transition messages in `routes/views.py` ("Driver already has an active
 *   route.", "Stop not found.", …) are direct `Response` returns with **no** code.
 * - `ORDER_NOT_GEOCODED` is emitted with a code from `orders/views.py`, but the byte-identical
 *   message from `routes/views.py:2193` has **none**.
 *
 * So the prose branch is load-bearing, not legacy decoration. Each entry is removable only once
 * its specific raise site emits a code — deleting one early silently degrades the message to
 * the generic fallback, and no test can catch that.
 */

import type { TFunction } from "i18next";

import i18n from "@/lib/i18n";
import {
  formatDateOnly,
  formatDayMonthSmartYear,
  formatNumber,
  formatVolume,
} from "@/lib/format";
import { buildIneligiblePicksParams } from "@/lib/ineligiblePicks";
import { buildStaleBuildParams } from "@/lib/buildChanges";

/**
 * The subset of i18next's `t` this module needs: a key plus optional interpolation. The real
 * `useTranslation().t` satisfies it and so does a `(key) => key` stub, which keeps the helper
 * testable without standing up i18next.
 */
export type TranslateFn = (
  key: string,
  options?: Record<string, unknown>,
) => string;

/** i18next's own `TFunction` is assignable to {@link TranslateFn}; this documents the intent. */
export type AnyTranslate = TranslateFn | TFunction;

/** The generic last-resort message key. Reached only when the server said nothing usable. */
export const GENERIC_ERROR_KEY = "common.errorOccurred";

/**
 * Stable `code` → i18n key.
 *
 * Keys are the SCREAMING_SNAKE values from `apps/core/error_codes.py` — that file is the
 * source of truth and these spellings must match it exactly. Codes absent from this map fall
 * through to `detail`, which is the correct behaviour: a code we have no copy for is not an
 * error, it just means the backend's own `detail` is the better message.
 *
 * BUG-463 (fixed, core `b4a1a70`): `routes/views.py` used to hand-write a lowercase
 * `order_cannot_be_placed` into a body literal — a second spelling of the condition the registry
 * already calls `ROUTE_ORDER_UNPLACEABLE`. The backend now sends only the registry value, so there
 * is no lowercase entry in this map and none is needed. Lookups stay case-normalized (see
 * `rawCode.toUpperCase()` below), which keeps a client on an older backend working; that
 * normalization is the compatibility mechanism, not a map entry, so there is nothing here to
 * delete.
 */
export const ERROR_CODE_MAP: Record<string, string> = {
  // --- Routes -------------------------------------------------------------
  ORDER_CANNOT_BE_PLACED: "routes.serverErrors.orderCannotBePlaced",
  ROUTE_ORDER_NOT_ELIGIBLE: "buildRoute.serverErrors.orderNotEligible",
  ROUTE_VEHICLE_NOT_ELIGIBLE: "buildRoute.serverErrors.vehicleNotEligible",
  ROUTE_CAPACITY_EXCEEDED: "buildRoute.serverErrors.orderCapacityExceeded",
  ROUTE_ORDER_UNPLACEABLE: "buildRoute.serverErrors.ordersCantFit",
  // BUG-672: the lock-free auto-build saw its orders or vans change between planning
  // and the commit transaction, so it wrote nothing. Reaches this map through the
  // build poller (`translatePlanningTaskError` with the task payload's `code`). The
  // sentence is the one the ROUTES_BUILD_FAILED notification already shows, reused
  // rather than duplicated, so the toast and the notification cannot drift apart.
  ROUTE_BUILD_STALE: "notifications.failureCodes.ROUTE_BUILD_STALE.body",
  // BUG-695: a build for a day already over is refused (HTTP 400 from all three
  // build endpoints, and as the failed task's `code`) with `params: {date, today}`;
  // the figures come from CODE_PARAM_BUILDERS below.
  ROUTE_DATE_IN_PAST: "buildRoute.serverErrors.dateInPast",

  // --- Planning -----------------------------------------------------------
  PLANNING_TASK_NOT_FOUND: "buildRoute.serverErrors.taskNotFound",
  // BUG-640: absent from this map until TASK-5064, so `extractApiError` fell
  // through to the raw `non_field_errors` sentence and the dispatcher read
  // «Order count 1287 exceeds max_orders_per_run=1000.» — English, and an
  // instruction to nobody. The copy names the limit and says what to DO about
  // it; the figures come from CODE_PARAM_BUILDERS below.
  PLANNING_MAX_ORDERS_EXCEEDED: "buildRoute.serverErrors.maxOrdersExceeded",
  // BUG-679: OR-Tools refused the fleet model («CP Solver fail»). Arrives as the
  // build poller's `code` and the ROUTES_BUILD_FAILED / OPTIMIZATION_FAILED
  // notification's `extra_data.code`, with `params: {orders, vehicles}`.
  PLANNING_SOLVER_FAILED: "buildRoute.serverErrors.solverFailed",

  // --- Orders -------------------------------------------------------------
  ORDER_SELECTION_EMPTY: "buildRoute.serverErrors.mixedEmptySelection",
  ORDER_SELECTION_OUT_OF_SCOPE: "buildRoute.serverErrors.mixedNothingFound",
  // BUG-519: «Удалить» archives, so archiving an already-archived order is a
  // no-op the server answers with 409. Without this entry the dispatcher saw a
  // bare «Произошла ошибка» over a row that is plainly on screen.
  ORDER_ALREADY_ARCHIVED: "orders.serverErrors.alreadyArchived",

  // --- MFA (TASK-4747) ----------------------------------------------------
  // The backend words each of these WELL ("Your role is not permitted to enrol
  // in MFA."), which is exactly why the frontend's two placeholder strings —
  // "Could not start enrolment. Please try again." and "Something went wrong.
  // Please try again." — were pure noise: they replaced a real reason with an
  // instruction to repeat an action that will fail again for the same reason.
  //
  // Mapped by CODE rather than passed through as `detail` because `detail` is
  // English and this app is fully localized; the code is the stable contract
  // (the backend may reword `detail` freely) and it is what carries the reason
  // into a Russian sentence.
  MFA_ROLE_NOT_PERMITTED: "mfa.serverErrors.roleNotPermitted",
  MFA_ALREADY_ENABLED: "mfa.serverErrors.alreadyEnabled",
  MFA_NOT_ENROLLED: "mfa.serverErrors.notEnrolled",
  MFA_NOT_ENABLED: "mfa.serverErrors.notEnabled",
  MFA_CODE_INVALID: "mfa.codeIncorrect",
  // Emitted ONLY by the authenticated /disable/ and /recovery-codes/regenerate/
  // endpoints. The unauthenticated login-step verify deliberately keeps a single
  // generic 401 (anti-enumeration — owner decision 2026-08-07), so this code
  // never reaches `MfaChallengeForm` and no login-path branch may assume it.
  MFA_RECOVERY_CODE_ALREADY_USED: "mfa.serverErrors.recoveryCodeAlreadyUsed",

  // --- Uploads (Sprint 179) -----------------------------------------------
  // Both codes are emitted by the PROXIED upload path (`upload_fileobj_to_s3`
  // raises them, so all seven call sites report identically) and mapped by code
  // rather than passed through as `detail`, because `detail` is English ("File
  // is too large.") and this app is localized.
  //
  // UPLOAD_FILE_TOO_LARGE arrives from TWO different layers with two different
  // body shapes, and both resolve here:
  //   - Django (20-25 MB): {"file": ["..."], "extra_data": {max_bytes, size},
  //     "code": "..."} — a FIELD-error shape, so `detail` is absent entirely.
  //   - nginx (>25 MB):    {"detail": "...", "code": "..."} — no `extra_data`,
  //     because nginx cannot know the app's limit or the real body size and
  //     inventing either would make the error lie.
  // Mapping by code is what makes those two shapes one message. `extra_data` is
  // therefore OPTIONAL on this code — see `readUploadSizeErrorData`, which falls
  // back to the client's own constant when the numbers are absent.
  UPLOAD_FILE_TOO_LARGE: "uploads.errors.tooLarge",
  UPLOAD_FILE_TYPE_INVALID: "uploads.errors.invalidType",
  // BUG-726: the bytes are not an image although the name says `.jpg` — the
  // chat upload checks the content now. `extra_data.extension` is what the
  // file CLAIMED; the sentence does not need it.
  UPLOAD_FILE_NOT_IMAGE: "uploads.errors.notImage",

  // --- Chat WebSocket refusals (BUG-722 / BUG-728) -------------------------
  // `chat.error` frames carry these; resolved through `translateServerMessage`
  // so a WS refusal reads like an HTTP one.
  CHAT_REPLY_TARGET_INVALID: "chat.errors.replyTargetInvalid",
  CHAT_MESSAGE_NOT_SAVED: "chat.errors.notSaved",

  // --- Users / invitations (Sprint 178) -----------------------------------
  // Emitted by BOTH user-keyed invite routes — `POST /v1/users/{id}/invite/`
  // (tenant-scoped) and `POST /v1/admin/users/{id}/invite/` — when the member
  // already holds an unused, unexpired invitation, so no duplicate is minted.
  // Mapped by code rather than passed through as `detail` because `detail` is
  // English and this app is localized. The reason matters here: an admin who
  // reads "failed" retries forever, when in fact the invitation they wanted
  // already exists and the member simply has not opened it yet.
  ADMIN_INVITATION_ALREADY_VALID: "users.serverErrors.invitationAlreadyValid",

  // --- Capacity (BUG-536) -------------------------------------------------
  // `routes/views.py::_capacity_response` answers 409 with a LOWERCASE code
  // (`capacity_volume_exceeded` / `capacity_weight_exceeded`) — it is a body
  // literal, not an `ErrorCode` registry member, so it is spelled here in the
  // SCREAMING_SNAKE form the `rawCode.toUpperCase()` lookup produces.
  //
  // These two are the reason BUG-536 existed: with no entry the resolver fell
  // through to `detail`, which is English prose, and the dispatcher got a
  // Russian toast heading over an English sentence.
  //
  // Both messages are COMPOSED FROM THE STRUCTURED FIELDS (`current`, `adding`,
  // `limit`, `kind`) via {@link CODE_PARAM_BUILDERS}, never by patching the
  // English sentence — BUG-457 established prose-matching as the wrong
  // approach, and the backend sends the numbers separately for exactly this
  // reason.
  CAPACITY_VOLUME_EXCEEDED: "routes.serverErrors.capacityVolumeExceeded",
  CAPACITY_WEIGHT_EXCEEDED: "routes.serverErrors.capacityWeightExceeded",

  // --- Auth / tenant (BUG-535, mapped by BUG-536) -------------------------
  // Emitted by the tenant gate on EVERY authenticated request once a dealer
  // deactivates the company (403), and by the login endpoint itself. Mapped
  // because `detail` is English ("This company account is deactivated. Contact
  // your administrator.") and it is the one message a locked-out user reads.
  AUTH_TENANT_SUSPENDED: "auth.serverErrors.tenantSuspended",

  // --- The rest of the BUG-536 audit -------------------------------------
  // Three more codes a DISPATCHER reaches whose `default_detail` is FIXED (no
  // caller-supplied sentence, no interpolated ids), so one static translation
  // reproduces the backend's message exactly and loses nothing.
  //
  // That last property is the whole selection criterion, and it is why the
  // other ~112 unmapped codes are deliberately left alone rather than padded
  // in. Two groups must NOT be mapped:
  //   - Umbrella codes where the CALLER supplies the sentence — ROUTE_STATUS_INVALID
  //     covers start/complete/archive/release/re-optimize, and the backend's own
  //     docstring calls four of those sentences "PROSE PINS" matched by this file.
  //     Mapping the code would replace "Route must be in 'planned' status to
  //     start." with the vague umbrella default and LOSE information.
  //   - Payload-only codes that are never an envelope `code` at all: the whole
  //     UNASSIGNED_* family (the solver's raw lowercase reason travels inside
  //     `unassigned[]`, translated via `routes.buildAlert.reasons.*`) plus
  //     PLANNING_MATRIX_UNAVAILABLE and the notification-side INTERNAL_ERROR.
  //     (INTERNAL_ERROR and VALIDATION_ERROR can now arrive via the build poller's
  //     task payload; left unmapped on purpose so they keep falling through to the
  //     task's own `error` text, the pre-BUG-672 behaviour.)
  ROUTE_ALREADY_CANCELLED: "routes.serverErrors.alreadyCancelled",
  ROUTE_NOT_DISBANDABLE: "routes.serverErrors.notDisbandable",
  // Proof-of-delivery guards (BUG-532 / BUG-534). All three refuse a gesture that would
  // destroy a DeliveryConfirmation. Unmapped until now, so they fell through to the raw
  // English `detail` — the BUG-536 symptom. The two per-stop codes stay distinct because
  // the affordance the client disables differs (a drag handle vs a delete button), while
  // the whole-route gesture has no stop-level affordance and gets one code for both
  // endpoints.
  ROUTE_MOVE_STOP_CLOSED: "routes.serverErrors.moveStopClosed",
  ROUTE_REMOVE_STOP_CLOSED: "routes.serverErrors.removeStopClosed",
  ROUTE_HAS_CLOSED_STOPS: "routes.serverErrors.routeHasClosedStops",
  ORDER_TRACKING_LINK_UNAVAILABLE:
    "orders.serverErrors.trackingLinkUnavailable",
  // Parcels switched off for the tenant. Reached from the labels/parcel-scan
  // surfaces, which a dispatcher opens directly from a route.
  FEATURE_DISABLED: "routes.serverErrors.parcelsDisabled",

  // --- Permanent routes / автолавки (Sprint 191) --------------------------
  // Mapped by CODE, never by string-matching `detail`. BUG-536 was exactly this
  // failure mode: the backend emitted stable codes, the frontend never mapped
  // them, and the dispatcher read raw English prose in a Russian UI. `check:i18n`
  // is structurally blind to it — a code that falls through to `detail` renders
  // real (English) text, so nothing in any gate looks wrong.
  //
  // Each message names the REPAIR, not just the refusal. ROUTE_EXISTS is the one
  // that matters most: it is not "fix the body" but "edit the route itself",
  // which is a different action on a different screen — a dispatcher told only
  // «нельзя» would keep retrying the schedule.
  ROUTE_SCHEDULE_EXCEPTION_ROUTE_EXISTS:
    "routeSchedules.serverErrors.routeExists",
  ROUTE_SCHEDULE_EXCEPTION_PAST_DATE: "routeSchedules.serverErrors.pastDate",
  ROUTE_SCHEDULE_EXCEPTION_DUPLICATE_DATE:
    "routeSchedules.serverErrors.duplicateDate",
  ROUTE_SCHEDULE_EXCEPTION_INVALID: "routeSchedules.serverErrors.invalid",
  ROUTE_SCHEDULE_RELEASE_TIME_REQUIRED:
    "routeSchedules.serverErrors.releaseTimeRequired",
  // A SEPARATE code from the release one above, deliberately so on both sides:
  // the two repairs name two different inputs, and a client that cannot tell
  // them apart cannot put the error on the right field.
  ROUTE_SCHEDULE_START_TIME_REQUIRED:
    "routeSchedules.serverErrors.startTimeRequired",
  ROUTE_SCHEDULE_PERIOD_INVALID: "routeSchedules.serverErrors.periodInvalid",
  ROUTE_TEMPLATE_VERSION_PAST_DATE:
    "routeSchedules.serverErrors.versionPastDate",
  ROUTE_TEMPLATE_VERSION_DUPLICATE_DATE:
    "routeSchedules.serverErrors.versionDuplicateDate",
  ROUTE_DRIVER_NOT_ELIGIBLE: "routeSchedules.serverErrors.driverNotEligible",
  CALENDAR_YEAR_NOT_COVERED: "calendar.serverErrors.yearNotCovered",

  // --- Infrastructure (TASK-4984 / BUG-578) -------------------------------
  // Emitted by the 503 envelope TASK-4983 added around a database failure
  // (`OperationalError` / `InterfaceError`) and by `handler500`'s JSON fallback.
  //
  // This is the failure class where a readable message matters MOST and, until
  // this entry existed, produced none: the body used to be Django's 145-byte
  // HTML error page, `extractApiError` fell through to `common.errorOccurred`,
  // and the dispatcher concluded "nothing was deleted" while 96 of 100 rows had
  // in fact been archived. The message names the condition AND the action
  // (retry shortly), because the one useful thing to do here is wait, not retry
  // immediately and add to the load that caused it.
  SERVICE_UNAVAILABLE: "common.serverErrors.serviceUnavailable",

  // --- BUG-677: the rest of the codes a person actually reaches ------------
  // Owner: «чтобы перевод на русский был, а то часть ошибок системных на англ».
  // Every code below fell through to `detail`, i.e. an English sentence in a
  // Russian UI. Audited against each raise site in core (not guessed):
  //   - a code whose detail is FIXED gets one static sentence (nothing lost);
  //   - a code whose caller supplies several sentences is listed in
  //     PROSE_FIRST_CODES, so the specific prose translation wins and this
  //     entry is only the fallback for a sentence the prose maps do not know;
  //   - a code that needs figures gets them from CODE_PARAM_BUILDERS.
  // Deliberately NOT mapped (a client bug, never a user condition — the UI
  // builds those params itself): ROUTE_LABEL_FORMAT_INVALID,
  // ROUTE_DATE_PARAM_INVALID, PLANNING_ROUTE_INDEX_INVALID,
  // ORDER_EXPORT_FORMAT_INVALID, ORDER_QUERY_PARAM_INVALID,
  // BILLING_PERIOD_INVALID, BILLING_REPORT_PARAM_INVALID,
  // TRACKING_QUERY_PARAM_INVALID, GEOFENCE_POINT_INVALID, PARSE_ERROR,
  // METHOD_NOT_ALLOWED, NOT_ACCEPTABLE, UNSUPPORTED_MEDIA_TYPE; and the
  // public-API-only API_KEY_SCOPE_MISSING / PUBLIC_* / DEALER_TENANTS_NOT_FOUND /
  // DEALER_REPORT_RANGE_INVALID (TASK-5117: machine-to-machine codes of the
  // dealer billing report that no cabinet screen ever receives).
  //
  // Generic DRF conditions. PROSE_FIRST: "Order not found." etc. stay specific.
  PERMISSION_DENIED: "common.serverErrors.permissionDenied",
  TENANT_SCOPE_REQUIRED: "common.serverErrors.permissionDenied",
  TENANT_SCOPE_FORBIDDEN: "common.serverErrors.permissionDenied",
  NOT_FOUND: "common.serverErrors.notFound",
  NOT_AUTHENTICATED: "common.serverErrors.notAuthenticated",
  THROTTLED: "common.serverErrors.throttled",
  // Plan limits. The three QuotaExceeded sentences carry the figures; see
  // PROSE_WITH_PARAMS. This entry is the figure-less fallback.
  QUOTA_EXCEEDED: "common.serverErrors.quotaExceeded",
  // BUG-674: the auto build's routes-per-day refusal; figures from `params`.
  ROUTE_QUOTA_EXHAUSTED: "routes.serverErrors.quotaExhausted",

  // Auth / users.
  AUTH_INVALID_CREDENTIALS: "login.invalidCredentials",
  AUTH_LOGIN_THROTTLED: "auth.serverErrors.loginThrottled",
  AUTH_RESET_THROTTLED: "forgotPassword.tooManyRequests",
  AUTH_CURRENT_PASSWORD_INCORRECT: "auth.serverErrors.currentPasswordIncorrect",
  AUTH_PASSWORD_TOO_WEAK: "auth.serverErrors.passwordTooShort",
  AUTH_TOKEN_INVALID: "auth.serverErrors.tokenInvalid",
  AUTH_ROLE_NOT_ASSIGNABLE: "users.serverErrors.roleNotAssignable",
  AUTH_USER_ALREADY_ACTIVE: "users.serverErrors.userAlreadyActive",
  AUTH_EMAIL_TAKEN: "users.serverErrors.emailTaken",
  ACCOUNT_DELETION_ACTIVE_ROUTE: "users.serverErrors.deletionActiveRoute",
  ACCOUNT_DELETION_CONFIRMATION_INVALID:
    "users.serverErrors.deletionConfirmationInvalid",
  ACCOUNT_DELETION_ALREADY_ERASED: "users.serverErrors.deletionAlreadyErased",
  // Sprint 218 — Team page guards (TASK-5095).
  USER_CANNOT_DISABLE_SELF: "users.serverErrors.cannotDisableSelf",
  USER_CANNOT_CHANGE_OWN_ROLE: "users.serverErrors.cannotChangeOwnRole",
  USER_CANNOT_DELETE_SELF: "users.serverErrors.cannotDeleteSelf",
  USER_LAST_COMPANY_ADMIN: "users.serverErrors.lastCompanyAdmin",
  USER_ADMIN_ROLE_READ_ONLY: "users.serverErrors.adminRoleReadOnly",
  USER_NOT_INVITED: "users.serverErrors.notInvited",
  USER_NOT_ACTIVE: "users.serverErrors.notActive",

  // Routes.
  ROUTE_VEHICLE_WRONG_TENANT: "routes.serverErrors.vehicleNotFound",
  ROUTE_WAREHOUSE_WRONG_TENANT: "routes.serverErrors.warehouseNotFound",
  ROUTE_VEHICLE_REQUIRED: "routes.serverErrors.vehicleRequiredManual",
  ROUTE_STOP_TIME_INVALID: "routes.serverErrors.departureBeforeArrival",
  ROUTE_SCAN_CODE_INVALID: "routes.serverErrors.scanCodeInvalid",
  ROUTE_REORDER_INVALID: "routes.serverErrors.reorderInvalid",
  ROUTE_MOVE_STOP_INVALID: "routes.serverErrors.moveStopInvalid",
  ROUTE_MOVE_STOP_CONFLICT: "routes.serverErrors.moveStopConflict",
  ROUTE_CONFIRMATION_REQUIRED: "routes.serverErrors.confirmationRequired",
  ROUTE_STOP_POSITION_CONFLICT: "routes.serverErrors.stopPositionConflict",
  ROUTE_VEHICLE_CHECK_BLOCKED: "routes.serverErrors.vehicleCheckBlocked",
  ROUTE_BATCH_SELECTION_INVALID: "routes.serverErrors.batchSelectionEmpty",
  TRACKING_SESSION_NOT_FOUND: "routes.serverErrors.trackingSessionNotFound",

  // Planning.
  PLANNING_MIXED_DELIVERY_DATES: "buildRoute.serverErrors.allOrdersSameDate",
  PLANNING_WAREHOUSE_REQUIRED: "buildRoute.serverErrors.warehouseRequired",
  PLANNING_WAREHOUSE_NOT_ELIGIBLE:
    "buildRoute.serverErrors.warehouseNotEligible",
  PLANNING_TASK_NOT_COMPLETED: "buildRoute.serverErrors.taskNotCompleted",
  PLANNING_ALREADY_CONFIRMED: "buildRoute.serverErrors.alreadyConfirmed",

  // Orders / permanent orders / saved filters / vehicles.
  ORDER_NOT_PERMANENT: "orders.serverErrors.notPermanent",
  ORDER_TEMPLATE_SELECTION_OUT_OF_SCOPE:
    "orders.serverErrors.templatesNotFound",
  ORDER_TEMPLATE_SCHEDULE_EMPTY: "orders.serverErrors.scheduleEmpty",
  ORDER_TEMPLATE_SCHEDULE_PAST: "orders.serverErrors.schedulePast",
  GEOCODING_ADDRESS_EMPTY: "orders.serverErrors.noAddressToGeocode",
  SAVED_FILTER_DUPLICATE_NAME: "orders.serverErrors.savedFilterDuplicate",
  VEHICLE_DUPLICATE_REGISTRATION: "vehicles.serverErrors.duplicateRegistration",

  // Billing / settings / admin.
  BILLING_RECORD_EMPTY: "billing.serverErrors.recordEmpty",
  BILLING_RECORD_FINAL: "billing.serverErrors.recordFinal",
  BILLING_PDF_NOT_READY: "billing.pdfNotReady",
  BILLING_TENANT_SELECTION_EMPTY: "billing.serverErrors.tenantSelectionEmpty",
  SETTINGS_REORDER_INVALID: "common.serverErrors.reorderStale",
  NOTIFICATION_DEFAULT_TEMPLATE_MISSING:
    "common.serverErrors.defaultTemplateMissing",
  ADMIN_IMPERSONATION_NESTED: "common.serverErrors.impersonationNested",
  ADMIN_DEALER_HAS_TENANTS: "common.serverErrors.dealerHasTenants",
  WHITELABEL_DOMAIN_CONFLICT: "common.serverErrors.dealerDomainExists",
};

/**
 * Codes whose `detail` is supplied by the CALLER and varies per raise site (BUG-677).
 *
 * For these the code alone is an umbrella ("the reorder was invalid"), while the
 * sentence names the actual cause ("Duplicate positions."). When the prose maps
 * know the sentence, that specific translation wins; the {@link ERROR_CODE_MAP}
 * entry is only the fallback. Before this set existed the choice was between
 * mapping the code (losing the cause) and not mapping it (English) — BUG-536 chose
 * English; the owner's ruling for BUG-677 is that neither is acceptable.
 */
const PROSE_FIRST_CODES = new Set([
  "NOT_FOUND",
  "PERMISSION_DENIED",
  "QUOTA_EXCEEDED",
  "ROUTE_SCAN_CODE_INVALID",
  "ROUTE_REORDER_INVALID",
  "ROUTE_MOVE_STOP_INVALID",
  "ROUTE_MOVE_STOP_CONFLICT",
  "PLANNING_WAREHOUSE_NOT_ELIGIBLE",
  "ORDER_TEMPLATE_SCHEDULE_EMPTY",
]);

/**
 * Numbers a decimal-string capacity field carried, parsed defensively.
 *
 * DRF `DecimalField`s serialize as STRINGS (`"2.000"`, `"0.500"`) — see the
 * DEV_RULES note on decimal strings — so every one of these must be parsed
 * before it can be formatted. A field that is absent or unparseable yields
 * `null`, which the builder below turns into a dash rather than "NaN".
 */
function readDecimal(source: Record<string, unknown>, key: string): number {
  const raw = source[key];
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : NaN;
  if (typeof raw !== "string" || raw.trim() === "") return NaN;
  return Number(raw);
}

/**
 * Formats one capacity figure with the unit its `kind` implies.
 *
 * Volume goes through {@link formatVolume}, the app's existing volume
 * formatter, so a capacity toast renders "2 м³" exactly like every order row
 * and vehicle card does — including the trailing-zero trim that turns the
 * backend's `"2.000"` into `2`. Weight follows the same shape the rest of the
 * app uses for kilograms (`Intl.NumberFormat` + the `units.kg` key), because
 * there is no `formatWeight` helper to reuse.
 *
 * The volume unit is deliberately left to `formatVolume`'s default (`m3` →
 * `units.m3`) rather than the tenant's `volume_unit` setting: this module is
 * pure and has no access to the settings query, and the figures the backend
 * compares here ARE the `volume_m3` columns. Relabelling them with a tenant
 * unit we cannot read would be a guess; `m³` is what the numbers actually are.
 */
function formatCapacityFigure(
  value: number,
  kind: "volume" | "weight",
): string {
  if (!Number.isFinite(value)) return "—";
  if (kind === "volume") return formatVolume(value);
  return `${new Intl.NumberFormat(i18n.language).format(value)} ${i18n.t("units.kg")}`;
}

/**
 * Interpolation params for the codes whose message needs numbers off the body.
 *
 * {@link ERROR_CODE_MAP} alone can only render a STATIC sentence — `t(key)`
 * with no arguments. A capacity refusal that does not name the figures is
 * nearly useless ("it does not fit" — by how much? which vehicle would?), and
 * the backend already sends `current` / `adding` / `limit` as separate fields
 * precisely so a translated sentence can be composed rather than string-patched
 * out of the English one (BUG-457, BUG-536).
 *
 * A builder receives the whole response body and returns the params for its
 * key. Codes absent from this map are rendered with no params, which is the
 * existing behaviour for all ~78 other entries.
 */
const CODE_PARAM_BUILDERS: Record<
  string,
  (body: Record<string, unknown>) => Record<string, unknown>
> = {
  CAPACITY_VOLUME_EXCEEDED: (body) => buildCapacityParams(body, "volume"),
  CAPACITY_WEIGHT_EXCEEDED: (body) => buildCapacityParams(body, "weight"),
  PLANNING_MAX_ORDERS_EXCEEDED: buildMaxOrdersParams,
  ROUTE_QUOTA_EXHAUSTED: buildRouteQuotaParams,
  ROUTE_DATE_IN_PAST: buildRouteDateInPastParams,
  PLANNING_SOLVER_FAILED: buildSolverFailedParams,
  THROTTLED: buildThrottleParams,
  ADMIN_DEALER_HAS_TENANTS: buildDealerTenantsParams,
  // BUG-686: a build NONE of whose picks can be planned names the picks and why
  // (`params: {count, orders: [{order_id, reason, detail?}]}`).
  ROUTE_ORDER_NOT_ELIGIBLE: (body) =>
    buildIneligiblePicksParams(
      typeof body.params === "object" &&
        body.params !== null &&
        !Array.isArray(body.params)
        ? (body.params as Record<string, unknown>)
        : null,
      i18n.t,
    ),
  // BUG-687…693: a build none of whose routes could be written names the orders
  // that changed meanwhile and what changed (same `params` shape as above).
  ROUTE_BUILD_STALE: (body) =>
    buildStaleBuildParams(
      typeof body.params === "object" &&
        body.params !== null &&
        !Array.isArray(body.params)
        ? (body.params as Record<string, unknown>)
        : null,
      i18n.t,
    ),
};

/**
 * Codes whose message is chosen from the structured `extra_data` block, ahead of
 * both the prose maps and the static {@link ERROR_CODE_MAP} entry (Sprint 218).
 *
 * `QUOTA_EXCEEDED` is an umbrella over three plan limits. For the USERS seat the
 * backend now sends `extra_data: {resource: "users", limit, current, used}` as
 * JSON ints (TASK-5096), and the Team page promises one exact sentence —
 * «Достигнут лимит тарифа: 50 из 50 пользователей» — shared with the seat-limit
 * tooltip on «+ Добавить пользователя» (one key, two readers). A resolver returns
 * `null` when the block is absent or not for it, and the existing path
 * (prose, then the figure-less fallback) runs as before — vehicles and
 * routes-per-day are untouched.
 */
const CODE_EXTRA_DATA_RESOLVERS: Record<
  string,
  (
    extra: Record<string, unknown>,
  ) => { key: string; params: Record<string, unknown> } | null
> = {
  QUOTA_EXCEEDED: (extra) => {
    if (extra.resource !== "users") return null;
    const limit = readCount(extra.limit);
    const current = readCount(extra.current ?? extra.used);
    if (limit === null || current === null) return null;
    // `count` picks the plural of «пользователей» after «из {{limit}}».
    return {
      key: "users.seats.limitReached",
      params: { limit, current, count: limit },
    };
  },
};

/** Reads a non-negative integer off an unknown value (number or digit string). */
function readCount(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && /^\d+$/.test(value)) return Number(value);
  return null;
}

/**
 * BUG-674/677: the routes-per-day figures of a `ROUTE_QUOTA_EXHAUSTED` refusal.
 *
 * The backend sends them as `params: {limit, used, date}` (the build poller's task
 * payload and the failure notification both carry it). Without them the sentence
 * still says what is wrong and what to do, just without the numbers — never
 * "undefined", never a confident 0.
 */
function buildRouteQuotaParams(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const params =
    typeof body.params === "object" &&
    body.params !== null &&
    !Array.isArray(body.params)
      ? (body.params as Record<string, unknown>)
      : {};
  const limit = readCount(params.limit);
  const used = readCount(params.used);
  const date = typeof params.date === "string" ? params.date : null;
  if (limit === null || used === null || date === null) return {};
  return {
    limit: formatNumber(limit),
    used: formatNumber(used),
    // «на 18 окт.» — the year only when it is not the current one.
    date: formatDayMonthSmartYear(date),
    // `count` selects the plural of «маршрута/маршрутов» after «из {{limit}}»;
    // `context` selects the sentence that names the figures.
    count: limit,
    context: "figures",
  };
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * BUG-695: the two dates of a `ROUTE_DATE_IN_PAST` refusal — `params: {date,
 * today}` (ISO, tenant-local). «Нельзя строить маршрут на прошедшую дату
 * (25 мая). Сегодня 20 окт. — выберите сегодня или позже». Without both dates
 * the figure-less sentence renders — never "undefined".
 */
function buildRouteDateInPastParams(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const params =
    typeof body.params === "object" &&
    body.params !== null &&
    !Array.isArray(body.params)
      ? (body.params as Record<string, unknown>)
      : {};
  const date = typeof params.date === "string" ? params.date.trim() : "";
  const today = typeof params.today === "string" ? params.today.trim() : "";
  if (!ISO_DATE.test(date) || !ISO_DATE.test(today)) return {};
  return {
    date: formatDayMonthSmartYear(date),
    today: formatDayMonthSmartYear(today),
    context: "figures",
  };
}

/**
 * BUG-679: the figures of a `PLANNING_SOLVER_FAILED` refusal — `params: {orders,
 * vehicles}`. Each is worded with its own plural («для 21 заказа на 3 машинах»),
 * which one `count` cannot select for both, so the two phrases are composed here.
 * Without both figures the figure-less sentence renders.
 */
function buildSolverFailedParams(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const params =
    typeof body.params === "object" &&
    body.params !== null &&
    !Array.isArray(body.params)
      ? (body.params as Record<string, unknown>)
      : {};
  const orders = readCount(params.orders);
  const vehicles = readCount(params.vehicles);
  if (orders === null || vehicles === null) return {};
  return {
    orders: i18n.t("buildRoute.serverErrors.solverFailedOrders", {
      count: orders,
    }),
    vehicles: i18n.t("buildRoute.serverErrors.solverFailedVehicles", {
      count: vehicles,
    }),
    context: "figures",
  };
}

/**
 * The wait DRF names in its own throttle sentence ("Request was throttled.
 * Expected available in 20 seconds."). The `Retry-After` header is not visible
 * here, so the figure is read off the sentence; the copy works without it.
 */
function buildThrottleParams(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const detail = typeof body.detail === "string" ? body.detail : "";
  const seconds = /available in (\d+) seconds?/.exec(detail)?.[1];
  return seconds === undefined ? {} : { seconds, context: "figures" };
}

/** The tenant count the dealer-delete refusal names ("still owns 3 tenant(s)"). */
function buildDealerTenantsParams(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const detail = typeof body.detail === "string" ? body.detail : "";
  const tenants = /still owns (\d+) tenant/.exec(detail)?.[1];
  // `count` selects the plural of the noun after the figure (en «1 assigned
  // company»); with `context` it resolves `…dealerHasTenants_figures_one/_other`.
  // The ru copy is the «label: N» form and has a single `_figures` value, which
  // i18next falls back to when no plural form of it exists.
  return tenants === undefined
    ? {}
    : { tenants, count: Number(tenants), context: "figures" };
}

/**
 * The two figures the run-cap sentence needs: how many were selected, and the cap.
 *
 * Unlike the capacity pair, the backend does NOT send these as separate fields —
 * both build paths (`planning/views.py` and `orders/views.py`) key the refusal on
 * `non_field_errors` with one English sentence, "Order count 1287 exceeds
 * max_orders_per_run=1000.", and nothing else. So they are parsed out of it.
 *
 * Parsing a server sentence is normally the thing §6.4 forbids, and it is confined
 * here deliberately: the sentence is NOT what gets shown — the translated copy is,
 * and it renders with or without the numbers. A reworded backend message therefore
 * degrades this to a limit-less sentence that still tells the dispatcher what to do,
 * instead of reverting to raw English. {@link ERROR_CODE_MAP} still owns the
 * code→key decision; this only enriches it.
 *
 * i18next's `context` option is what selects the copy: `context: "figures"` resolves
 * `…maxOrdersExceeded_figures_*` (the sentence naming both numbers, its noun in the
 * plural form `count` = the limit selects) and no context
 * resolves the base `…maxOrdersExceeded` (the same instruction without them). A
 * reworded backend sentence therefore degrades to still-useful translated copy and
 * can never render "undefined" or a confident 0.
 */
function buildMaxOrdersParams(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const sentence =
    flatten(body.non_field_errors) ??
    (typeof body.detail === "string" ? body.detail : null) ??
    "";
  const selected = /Order count (\d+)/.exec(sentence)?.[1];
  const limit = /max_orders_per_run=(\d+)/.exec(sentence)?.[1];
  if (selected === undefined || limit === undefined) return {};
  // `count` = the limit: the noun after it agrees with the limit («не больше 21
  // заказа»), and i18next combines context and plural, so this resolves
  // `…maxOrdersExceeded_figures_one/_few/_many/_other`. The selected figure keeps
  // its own name — «выбрано {{selected}}» has no noun to agree with it.
  return { selected, limit, count: Number(limit), context: "figures" };
}

/**
 * Composes the four figures a capacity sentence needs.
 *
 * `kind` comes from the body when present and from the code otherwise, so a
 * response that omits it still picks the right unit. `total` is derived
 * (`current + adding`) rather than requested from the backend, which does not
 * send it — it is what makes the sentence readable: «2 м³ не помещаются в
 * 0,5 м³» is the fact the dispatcher acts on.
 */
function buildCapacityParams(
  body: Record<string, unknown>,
  fallbackKind: "volume" | "weight",
): Record<string, unknown> {
  const rawKind = typeof body.kind === "string" ? body.kind : null;
  const kind: "volume" | "weight" =
    rawKind === "volume" || rawKind === "weight" ? rawKind : fallbackKind;

  const current = readDecimal(body, "current");
  const adding = readDecimal(body, "adding");
  const limit = readDecimal(body, "limit");
  const total = current + adding;

  return {
    current: formatCapacityFigure(current, kind),
    adding: formatCapacityFigure(adding, kind),
    limit: formatCapacityFigure(limit, kind),
    total: formatCapacityFigure(total, kind),
  };
}

/**
 * Backend English `detail` sentences → i18n keys, for raise sites with no `code` yet.
 *
 * Every entry here was verified against the backend as a bare `return Response({"detail": ...})`
 * that the exception handler never sees. Remove an entry only when its own raise site starts
 * emitting a code and that code is in {@link ERROR_CODE_MAP}.
 */
export const PROSE_ERROR_MAP: Record<string, string> = {
  // routes/views.py — route status transitions. None of these carry a code.
  "Driver already has an active route.":
    "routes.serverErrors.driverHasActiveRoute",
  "Route must be in 'planned' status to start.":
    "routes.serverErrors.mustBePlannedToStart",
  "A vehicle must be assigned before starting the route.":
    "routes.serverErrors.vehicleRequired",
  "Route must be in 'active' status to complete.":
    "routes.serverErrors.mustBeActiveToComplete",
  "All stops must be in a terminal status (confirmed/rejected/skipped).":
    "routes.serverErrors.allStopsMustBeTerminal",
  "Route must be in 'completed' status to archive.":
    "routes.serverErrors.mustBeCompletedToArchive",
  "Order is already assigned to a planned or active route.":
    "routes.serverErrors.orderAlreadyInRoute",
  "Order not found.": "routes.serverErrors.orderNotFound",
  "Stop not found.": "routes.serverErrors.stopNotFound",
  // BUG-457: matched byte-for-byte, em dash included. `routes/views.py` sends
  // `code: ROUTE_ORDER_UNPLACEABLE` alongside (lowercase until BUG-463, core `b4a1a70`), so the
  // code branch normally wins and this stays the belt-and-braces path for a response that
  // somehow arrives without a code. Keep it: it costs nothing and it is the only thing standing
  // between a code-less 409 and a generic "an error occurred".
  "The order could not be placed on this route — its time window, capacity or distance constraints cannot be met. It stays unassigned.":
    "routes.serverErrors.orderCannotBePlaced",
  // planning/views.py — confirm/apply flow. No codes.
  "Already confirmed.": "buildRoute.serverErrors.alreadyConfirmed",
  "All orders must share the same delivery_date for auto strategy.":
    "buildRoute.serverErrors.allOrdersSameDate",
  "No available vehicles for the requested date.":
    "buildRoute.serverErrors.noVehiclesAvailable",
  // orders/serializers.py — these DO carry codes, kept for older deployments.
  "Select at least one order or permanent order.":
    "buildRoute.serverErrors.mixedEmptySelection",
  "No orders or permanent orders found for this tenant.":
    "buildRoute.serverErrors.mixedNothingFound",
  "Task not found or expired.": "buildRoute.serverErrors.taskNotFound",

  // --- BUG-677: caller-supplied sentences under an umbrella code ----------
  // Each verified against its raise site; the code is in PROSE_FIRST_CODES (or
  // has no map entry), so these win over the code's generic fallback.
  // routes/services.py `release` — ROUTE_VEHICLE_REQUIRED_FOR_ACTION / ROUTE_STATUS_INVALID.
  "A vehicle must be assigned before releasing the route to a driver.":
    "routes.serverErrors.vehicleRequiredRelease",
  "Only a planned route can be released.":
    "routes.serverErrors.onlyPlannedRelease",
  // routes/views.py reorder — ROUTE_REORDER_INVALID.
  "Duplicate positions.": "routes.serverErrors.reorderDuplicatePositions",
  "One or more stop IDs do not belong to this route.":
    "routes.serverErrors.reorderForeignStops",
  "One or more stop IDs do not belong to this template.":
    "routes.serverErrors.reorderForeignStops",
  // routes/services.py move-stop — ROUTE_MOVE_STOP_CONFLICT / ROUTE_MOVE_STOP_INVALID.
  "Orders can only be moved to planned or active routes.":
    "routes.serverErrors.moveTargetNotOpen",
  "Order is already a stop on the target route.":
    "routes.serverErrors.moveAlreadyOnTarget",
  "Order is not assigned to the source route.":
    "routes.serverErrors.moveNotOnSource",
  // routes/services.py scan — ROUTE_SCAN_CODE_INVALID (empty code).
  "A non-empty scan code is required.": "routes.serverErrors.scanCodeEmpty",
  // orders/services.py import — ORDER_IMPORT_FILE_INVALID (file-level, shown as a banner).
  "File is required.": "orders.serverErrors.importFileRequired",
  "File size must not exceed 10MB.": "orders.serverErrors.importFileTooLarge",
  "Only .csv and .xlsx files are supported.":
    "orders.serverErrors.importFileType",
  // orders/schedule.py — ORDER_TEMPLATE_SCHEDULE_EMPTY (two variants).
  "Select at least one weekday for repeat_mode 'weekdays'.":
    "orders.serverErrors.scheduleNoWeekdays",
  "Add at least one date for repeat_mode 'dates'.":
    "orders.serverErrors.scheduleNoDates",
  // DRF's own 403/404 defaults and GENERIC_FORBIDDEN_DETAIL.
  "You do not have permission to perform this action.":
    "common.serverErrors.permissionDenied",
  "Not found.": "common.serverErrors.notFound",
};

/**
 * Prose sentences that carry FIGURES, each with the params builder for its key (BUG-677).
 *
 * The figures exist only inside the sentence on these raise sites (no structured
 * field), so they are read off it — the same confined exception as
 * `buildMaxOrdersParams`: what is SHOWN is the translated copy, and a reworded
 * backend sentence degrades to the code's figure-less fallback, never to English
 * (the codes are in PROSE_FIRST_CODES or carry their own map entry).
 */
const PROSE_WITH_PARAMS: Array<
  [
    RegExp,
    string,
    (match: RegExpExecArray, t: TranslateFn) => Record<string, unknown>,
  ]
> = [
  // tenants/services.py — QUOTA_EXCEEDED, three resources.
  [
    /User quota exceeded: limit is (\d+) \(currently (\d+)\)/,
    "common.serverErrors.quotaUsers",
    (m) => ({ limit: m[1], current: m[2] }),
  ],
  [
    /Vehicle quota exceeded: limit is (\d+) \(currently (\d+)\)/,
    "common.serverErrors.quotaVehicles",
    (m) => ({ limit: m[1], current: m[2] }),
  ],
  [
    /Routes-per-day quota exceeded for (\d{4}-\d{2}-\d{2}): limit is (\d+) \(currently (\d+)\)/,
    "common.serverErrors.quotaRoutesPerDay",
    (m) => ({ date: formatDateOnly(m[1]), limit: m[2], current: m[3] }),
  ],
  // routes/services.py scan — ROUTE_SCAN_CODE_INVALID (unexpected code).
  [
    /^Code '(.+)' is not expected on this route\.$/,
    "routes.serverErrors.scanCodeUnexpected",
    (m) => ({ code: m[1] }),
  ],
  // routes/views.py reorder — ROUTE_REORDER_INVALID (positions taken).
  [
    /^Position\(s\) (.+) are already held by stops that are not part of this request\.$/,
    "routes.serverErrors.reorderPositionsTaken",
    (m) => ({ positions: m[1] }),
  ],
  // planning/views.py + planning/tasks.py — PLANNING_WAREHOUSE_NOT_ELIGIBLE.
  [
    /^Warehouse not found: (\d+)$/,
    "buildRoute.serverErrors.warehouseNotFound",
    (m) => ({ id: m[1] }),
  ],
  [
    /^Warehouse has no usable coordinates: (\d+)$/,
    "buildRoute.serverErrors.warehouseNoCoords",
    (m) => ({ id: m[1] }),
  ],
  // orders/services.py — ORDER_STATUS_INVALID (status machine).
  [
    /^Cannot transition order from '(\w+)' to '(\w+)'\.$/,
    "orders.serverErrors.statusTransition",
    (m, t) => ({
      // `defaultValue`: a status this bundle has no label for renders its raw
      // code rather than a key path.
      from: t(`orders.status.${m[1]}`, { defaultValue: m[1] }),
      to: t(`orders.status.${m[2]}`, { defaultValue: m[2] }),
    }),
  ],
];

/**
 * Regex prose matches, for sentences whose wording varies at the edges.
 * Checked after the exact map, before falling back to the raw text.
 */
const PROSE_REGEX_MAP: Array<[RegExp, string]> = [
  [
    /Selected orders exceed vehicle capacity/,
    "buildRoute.serverErrors.orderCapacityExceeded",
  ],
  [/not found or not eligible/, "buildRoute.serverErrors.orderNotEligible"],
  [/cannot fit any available vehicle/, "buildRoute.serverErrors.ordersCantFit"],
];

/**
 * Prose matches that carry the offending ids inline, e.g.
 * `"Orders missing coordinates (not geocoded yet): [12, 13]"`.
 *
 * These MUST stay prose-matched even though `ORDER_NOT_GEOCODED` exists as a code, because the
 * ids live only in the sentence — resolving such a response by code alone would produce a
 * correct but useless message ("some orders are not geocoded") and leave the dispatcher hunting
 * for which rows are broken. Each pattern captures the id list and interpolates it as `{{ids}}`.
 *
 * Consulted BEFORE {@link ERROR_CODE_MAP} for exactly this reason — see `extractApiError`.
 */
const PROSE_WITH_IDS: Array<[RegExp, string]> = [
  [
    /Templates missing coordinates \(not geocoded yet\):\s*\[?([^\]]*)\]?/,
    "buildRoute.serverErrors.templatesMissingCoords",
  ],
  [
    /Orders missing coordinates \(not geocoded yet\):\s*\[?([^\]]*)\]?/,
    "buildRoute.serverErrors.ordersMissingCoords",
  ],
  [
    /Orders are archived \(deleted\) and cannot be routed:\s*\[?([^\]]*)\]?/,
    "buildRoute.serverErrors.ordersArchived",
  ],
  // BUG-677: planning/tasks.py — ORDER_NOT_GEOCODED, every selected order lacks
  // coordinates (the optimization_failed notification's reason).
  [
    /None of the selected orders have usable coordinates \(not geocoded yet\):\s*\[?([^\]]*)\]?/,
    "buildRoute.serverErrors.noOrdersGeocoded",
  ],
  // BUG-677: planning/views.py — ORDER_STATUS_INVALID with the offending ids.
  [
    /Orders must have status 'new':\s*\[?([^\]]*)\]?/,
    "buildRoute.serverErrors.ordersNotNew",
  ],
  // BUG-677: routes/views.py batch add — ROUTE_ORDER_ALREADY_ASSIGNED with ids.
  [
    /Orders \[([^\]]*)\] are already assigned to another planned or active route/,
    "routes.serverErrors.ordersAlreadyInRoute",
  ],
];

/** Field keys that are form-level rather than tied to a single input. */
const NON_FIELD_KEYS = ["non_field_errors", "detail", "code"];

/**
 * Top-level body keys that are MESSAGE METADATA rather than DRF field errors,
 * keyed by the code that sends them (BUG-536).
 *
 * `_capacity_response` puts its structured numbers at the top level of the
 * body, beside `detail`, so the generic field-error loop would otherwise read
 * `{"kind": "volume", "current": "0", "adding": "2.000", …}` as five field
 * errors: `isFieldError` goes true and `applyApiErrorToForm` writes "volume"
 * and "0.500" into inputs as if they were validation messages.
 *
 * Scoped PER CODE rather than added to {@link NON_FIELD_KEYS} on purpose. Those
 * names are ordinary words — a serializer may legitimately have a `limit` or
 * `current` field, and a global exclusion would silently swallow a real
 * validation error on it. Only a body that identifies itself with a capacity
 * code gets these keys reinterpreted.
 */
const CODE_METADATA_KEYS: Record<string, readonly string[]> = {
  CAPACITY_VOLUME_EXCEEDED: [
    "kind",
    "current",
    "adding",
    "limit",
    "overridable",
  ],
  CAPACITY_WEIGHT_EXCEEDED: [
    "kind",
    "current",
    "adding",
    "limit",
    "overridable",
  ],
};

/** The structured result. Deliberately not a bare string — forms need the field map. */
export interface ApiErrorResult {
  /** The stable `code`, when the response carried one. `null` is normal, not anomalous. */
  code: string | null;
  /** The best available user-facing message. Always non-empty and always localized-or-verbatim. */
  message: string;
  /** Server field errors, keyed by backend field name, each flattened to one string. */
  fieldErrors: Record<string, string>;
  /** True when the server returned at least one field-specific error. */
  isFieldError: boolean;
  /** True when nothing usable came back and `message` is the generic fallback. */
  isGeneric: boolean;
}

/** Flattens a DRF field value (string, or array of strings) into one readable string. */
function flatten(value: unknown): string | null {
  if (typeof value === "string") return value.trim() === "" ? null : value;
  if (Array.isArray(value) && value.length > 0) {
    const parts = value
      .map((v) => (typeof v === "string" ? v : String(v)))
      .filter((v) => v.trim() !== "");
    return parts.length > 0 ? parts.join(" ") : null;
  }
  return null;
}

/**
 * Pulls the response body off an unknown throw without assuming it is an axios error.
 *
 * Written defensively on purpose: a non-axios throw, a network error with no `response`, an
 * HTML error page body (nginx 502), and a `response.data` that is a string or an array all
 * reach this helper in practice and none may throw.
 */
function getResponseData(err: unknown): unknown {
  if (err == null || typeof err !== "object") return undefined;
  if (!("response" in err)) return undefined;
  const response = (err as { response?: unknown }).response;
  if (response == null || typeof response !== "object") return undefined;
  if (!("data" in response)) return undefined;
  return (response as { data?: unknown }).data;
}

/**
 * Detects an HTML body (an nginx/proxy error page rather than a DRF JSON response).
 * Rendering raw markup to the user would be meaningless at best, so these fall through
 * to the generic message.
 */
function looksLikeHtml(text: string): boolean {
  const head = text.trimStart().slice(0, 200).toLowerCase();
  return (
    head.startsWith("<!doctype") ||
    head.startsWith("<html") ||
    head.startsWith("<?xml")
  );
}

/** Runs a prose string through the exact, regex and id-capturing maps. Returns null on no match. */
function translateProse(detail: string, t: TranslateFn): string | null {
  const exact = PROSE_ERROR_MAP[detail];
  if (exact) return t(exact);

  for (const [pattern, key] of PROSE_REGEX_MAP) {
    if (pattern.test(detail)) return t(key);
  }

  for (const [pattern, key] of PROSE_WITH_IDS) {
    const match = pattern.exec(detail);
    if (match !== null) return t(key, { ids: match[1].trim() });
  }

  for (const [pattern, key, buildParams] of PROSE_WITH_PARAMS) {
    const match = pattern.exec(detail);
    if (match !== null) return t(key, buildParams(match, t));
  }

  return null;
}

/**
 * Resolves any thrown API failure into a structured, user-facing result.
 *
 * Never throws: every branch is guarded, because this runs inside `onError` handlers where a
 * secondary exception would replace a bad error message with a blank screen.
 *
 * @param err Anything thrown by axios, TanStack Query, or application code.
 * @param t   An i18next `t`. Supply the real one so returned copy is localized.
 */
export function extractApiError(err: unknown, t: TranslateFn): ApiErrorResult {
  const generic = (): ApiErrorResult => ({
    code: null,
    message: t(GENERIC_ERROR_KEY),
    fieldErrors: {},
    isFieldError: false,
    isGeneric: true,
  });

  const data = getResponseData(err);

  // --- No response body at all: network failure, CORS, non-axios throw. ----
  if (data == null) {
    // An Error's own message is more informative than the generic string, but only when it
    // is real copy — axios's own "Network Error"/"Request failed with status code 500" are
    // developer strings that mean nothing to a dispatcher.
    if (err instanceof Error && err.message && !isAxiosNoise(err.message)) {
      return {
        code: null,
        message: err.message,
        fieldErrors: {},
        isFieldError: false,
        isGeneric: false,
      };
    }
    return generic();
  }

  // --- A body that is a bare string, or an HTML error page. ---------------
  if (typeof data === "string") {
    if (data.trim() === "" || looksLikeHtml(data)) return generic();
    const translated = translateProse(data, t);
    return {
      code: null,
      message: translated ?? data,
      fieldErrors: {},
      isFieldError: false,
      isGeneric: false,
    };
  }

  // --- A body that is a bare array: treated as non_field_errors. ----------
  if (Array.isArray(data)) {
    const flat = flatten(data);
    if (flat === null) return generic();
    return {
      code: null,
      message: translateProse(flat, t) ?? flat,
      fieldErrors: {},
      isFieldError: false,
      isGeneric: false,
    };
  }

  if (typeof data !== "object") return generic();

  const body = data as Record<string, unknown>;
  const rawCode = typeof body.code === "string" ? body.code : null;

  // Collect field errors first — needed by forms regardless of which branch wins the message.
  // Keys this code declares as message metadata (BUG-536) are skipped alongside the
  // universal non-field keys: they are numbers for composing the sentence, not input errors.
  const metadataKeys = rawCode
    ? (CODE_METADATA_KEYS[rawCode.toUpperCase()] ?? [])
    : [];
  const fieldErrors: Record<string, string> = {};
  for (const [key, value] of Object.entries(body)) {
    if (NON_FIELD_KEYS.includes(key) || metadataKeys.includes(key)) continue;
    const flat = flatten(value);
    if (flat !== null) fieldErrors[key] = flat;
  }
  const isFieldError = Object.keys(fieldErrors).length > 0;

  const detail = typeof body.detail === "string" ? body.detail : null;
  const nonField = flatten(body.non_field_errors);

  // The id-carrying messages are matched BEFORE the code branch: their ids exist only in the
  // prose, and `ORDER_NOT_GEOCODED` alone cannot say WHICH orders are broken.
  for (const candidate of [detail, ...Object.values(fieldErrors), nonField]) {
    if (candidate == null) continue;
    for (const [pattern, key] of PROSE_WITH_IDS) {
      const match = pattern.exec(candidate);
      if (match !== null) {
        return {
          code: rawCode,
          message: t(key, { ids: match[1].trim() }),
          fieldErrors,
          isFieldError,
          isGeneric: false,
        };
      }
    }
  }

  // BUG-677: field messages are what a form puts under its inputs, and they are
  // English on the wire (LANGUAGE_CODE is en-us, so even DRF's own "This field is
  // required." arrives in English). Each is run through the prose maps; one the
  // maps do not know stays verbatim. Done AFTER the id-capturing loop above,
  // which needs the raw sentences.
  const rawFieldErrors = { ...fieldErrors };
  const fieldNames = Object.keys(fieldErrors);
  for (const key of fieldNames) {
    fieldErrors[key] = translateProse(fieldErrors[key], t) ?? fieldErrors[key];
  }

  // --- 1. The stable code. -----------------------------------------------
  if (rawCode) {
    const normalized = rawCode.toUpperCase();
    const extra =
      typeof body.extra_data === "object" &&
      body.extra_data !== null &&
      !Array.isArray(body.extra_data)
        ? (body.extra_data as Record<string, unknown>)
        : null;
    const extraResolver = CODE_EXTRA_DATA_RESOLVERS[normalized];
    const resolvedFromExtra =
      extra && extraResolver ? extraResolver(extra) : null;
    if (resolvedFromExtra) {
      const message = t(resolvedFromExtra.key, resolvedFromExtra.params);
      if (fieldNames.length === 1) {
        fieldErrors[fieldNames[0]] = message;
      }
      return {
        code: rawCode,
        message,
        fieldErrors,
        isFieldError,
        isGeneric: false,
      };
    }
    const mapped = ERROR_CODE_MAP[normalized];
    if (mapped) {
      // An umbrella code whose caller wrote a specific sentence the prose maps
      // know: the specific translation wins (see PROSE_FIRST_CODES).
      const specific = PROSE_FIRST_CODES.has(normalized)
        ? [detail, nonField, ...Object.values(rawFieldErrors)]
            .filter((c): c is string => c != null && c.trim() !== "")
            .map((c) => translateProse(c, t))
            .find((c): c is string => c !== null)
        : undefined;
      // Most codes render a static sentence. A few (the capacity pair) need
      // figures the body carries as separate fields — see CODE_PARAM_BUILDERS.
      const buildParams = CODE_PARAM_BUILDERS[normalized];
      const message =
        specific ?? (buildParams ? t(mapped, buildParams(body)) : t(mapped));
      // A coded refusal on exactly ONE field is that field's error: the input
      // shows the same translated sentence instead of the server's English.
      if (fieldNames.length === 1) {
        fieldErrors[fieldNames[0]] = message;
      }
      return {
        code: rawCode,
        message,
        fieldErrors,
        isFieldError,
        isGeneric: false,
      };
    }
  }

  // --- 2. detail, through the prose maps, then verbatim. ------------------
  if (detail !== null && detail.trim() !== "") {
    return {
      code: rawCode,
      message: translateProse(detail, t) ?? detail,
      fieldErrors,
      isFieldError,
      isGeneric: false,
    };
  }

  // --- 3. DRF field errors. ----------------------------------------------
  if (isFieldError) {
    // Prefer the well-known build-route keys so the most specific message leads, then fall
    // back to the first field in body order.
    const preferred = ["template_ids", "order_ids"].find((k) => fieldErrors[k]);
    const lead = preferred
      ? fieldErrors[preferred]
      : Object.values(fieldErrors)[0];
    return {
      code: rawCode,
      message: translateProse(lead, t) ?? lead,
      fieldErrors,
      isFieldError: true,
      isGeneric: false,
    };
  }

  // --- 4. non_field_errors. ----------------------------------------------
  if (nonField !== null) {
    return {
      code: rawCode,
      message: translateProse(nonField, t) ?? nonField,
      fieldErrors,
      isFieldError: false,
      isGeneric: false,
    };
  }

  // --- 5. Generic fallback. ----------------------------------------------
  return { ...generic(), code: rawCode };
}

/**
 * Axios's own developer-facing messages, which must never be shown to a user.
 * `err.message` is otherwise a useful last resort for application-thrown errors.
 */
function isAxiosNoise(message: string): boolean {
  return (
    message === "Network Error" ||
    message === "canceled" ||
    /^Request failed with status code \d+$/.test(message) ||
    /^timeout of \d+ms exceeded$/.test(message)
  );
}

/**
 * The numbers an `UPLOAD_FILE_TOO_LARGE` response carried, when it carried any.
 *
 * `max_bytes` is the server's own limit and `size` the rejected file — both real
 * JSON integers (the backend assigns `detail` directly rather than through
 * `APIException.__init__`, precisely so DRF cannot stringify them).
 */
export interface UploadSizeErrorData {
  /** The server's limit in bytes, or `null` when the response omitted it. */
  maxBytes: number | null;
  /** The rejected file's size in bytes, or `null` when omitted. */
  size: number | null;
}

/** Reads an integer off an unknown record, or `null` when absent/not a number. */
function readInt(source: Record<string, unknown>, key: string): number | null {
  const value = source[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * Pulls `extra_data.max_bytes` / `extra_data.size` off a thrown upload failure.
 *
 * Exists because {@link extractApiError} deliberately reduces a body to a
 * message and a field map — it has no channel for structured params. The
 * "too large" message must name the ACTUAL limit rather than a number baked
 * into a translation string, and the server is the only authority on what that
 * limit currently is.
 *
 * Returns `null`s rather than throwing when the block is absent: `extra_data` is
 * genuinely optional on this code, because nginx's own 413 (>25 MB) cannot
 * populate it. Callers fall back to the client's mirrored constant, which is
 * correct in that case anyway — a body that big exceeded both limits.
 */
export function readUploadSizeErrorData(err: unknown): UploadSizeErrorData {
  const empty: UploadSizeErrorData = { maxBytes: null, size: null };
  const data = getResponseData(err);
  if (data == null || typeof data !== "object" || Array.isArray(data)) {
    return empty;
  }
  const extra = (data as Record<string, unknown>).extra_data;
  if (extra == null || typeof extra !== "object" || Array.isArray(extra)) {
    return empty;
  }
  const block = extra as Record<string, unknown>;
  return {
    maxBytes: readInt(block, "max_bytes"),
    size: readInt(block, "size"),
  };
}

/**
 * Reads `extra_data.allowed` off an `UPLOAD_FILE_TYPE_INVALID` response — the
 * extensions the server will actually accept, as a list it sent deliberately so
 * the client need not split an English sentence on commas.
 *
 * Returns `null` when the block is missing or malformed, so the caller falls
 * back to the client's mirrored list rather than rendering an empty set.
 */
export function readUploadAllowedExtensions(err: unknown): string[] | null {
  const data = getResponseData(err);
  if (data == null || typeof data !== "object" || Array.isArray(data)) {
    return null;
  }
  const extra = (data as Record<string, unknown>).extra_data;
  if (extra == null || typeof extra !== "object" || Array.isArray(extra)) {
    return null;
  }
  const allowed = (extra as Record<string, unknown>).allowed;
  if (!Array.isArray(allowed)) return null;
  const list = allowed.filter((v): v is string => typeof v === "string");
  return list.length > 0 ? list : null;
}

/**
 * Reads the HTTP status off a thrown API failure, or `null` when there was no
 * response at all (network failure, CORS, a non-axios throw).
 *
 * BUG-498: `ApiErrorResult` deliberately carries no status — it exists to
 * produce *copy*, and copy should key off `code`, not a number. But a few
 * surfaces must branch on the difference between "this resource does not
 * exist" (404 — retrying is pointless, say so) and "the request failed"
 * (anything else — offer a retry). That distinction is about which UI to
 * render, not which sentence to print, so it gets its own reader rather than
 * being smuggled into the message pipeline.
 */
export function readApiErrorStatus(err: unknown): number | null {
  if (err == null || typeof err !== "object") return null;
  const response = (err as { response?: unknown }).response;
  if (response == null || typeof response !== "object") return null;
  const status = (response as { status?: unknown }).status;
  return typeof status === "number" ? status : null;
}

/**
 * Convenience wrapper for the common `catch` that only wants a string to toast.
 * Prefer {@link extractApiError} when the caller is a form and needs `fieldErrors`.
 */
export function extractApiErrorMessage(
  err: unknown,
  t: TranslateFn,
  fallback?: string,
): string {
  const result = extractApiError(err, t);
  return result.isGeneric && fallback !== undefined ? fallback : result.message;
}

/** A server message resolved for a surface that has no HTTP error to inspect. */
export interface ServerMessageTranslation {
  message: string;
  /**
   * True when `message` came from this bundle (a code or prose key), false when it
   * is the server's own sentence passed through verbatim. A caller that would
   * otherwise show raw English as a whole sentence frames it instead (BUG-677).
   */
  translated: boolean;
}

/**
 * Resolves a bare server sentence plus optional registry `code` / `params` through
 * the SAME maps every HTTP error uses (BUG-677).
 *
 * For async results — the build poller's failed task payload and the failure
 * notifications — where the failure is a field in a 200 body or a WebSocket frame,
 * not a thrown axios error. Wrapping it as a response body keeps one code path.
 */
export function translateServerMessage(
  detail: string,
  t: TranslateFn,
  code?: string | null,
  params?: Record<string, unknown> | null,
): ServerMessageTranslation {
  const data: Record<string, unknown> = { detail };
  if (code) data.code = code;
  if (params) data.params = params;
  const result = extractApiError({ response: { data } }, t);
  return {
    message: result.message,
    translated: result.message !== detail && !result.isGeneric,
  };
}

/**
 * The `setError` shape react-hook-form exposes, narrowed to what this module uses.
 *
 * Field names are typed as a generic string union so a caller's `UseFormSetError<FormValues>`
 * is assignable without a cast: RHF types the first parameter as `FieldPath<T> | "root"`, and
 * an unconstrained `string` would not be. Callers therefore keep their compile-time guarantee
 * that a field name is real — a typo in a `fieldMap` value is still a type error.
 */
export type SetFormError<TField extends string = string> = (
  field: TField | "root",
  error: { type?: string; message: string },
) => void;

/** What {@link applyApiErrorToForm} did, so the caller can decide whether to also toast. */
export interface ApplyFormErrorResult extends ApiErrorResult {
  /** True when at least one message landed on a mapped input. */
  appliedToField: boolean;
  /** True when the message went to the form-level `root` error instead. */
  appliedToRoot: boolean;
}

/**
 * Applies a failed request's field errors to a react-hook-form, then puts anything left over
 * on `root`.
 *
 * This replaces ~15 hand-rolled `onError` loops across the admin modals. Those loops mapped
 * server fields onto inputs **correctly** — that behaviour is preserved exactly, including the
 * `fieldMap` indirection, because a backend field name does not always equal the form field
 * name. What they got wrong was uniform: every one of them ended at
 * `setError("root", t("common.errorOccurred"))`, discarding a perfectly good `detail` the
 * server had just sent. Here `root` receives the real message and falls back to the generic
 * string only when {@link extractApiError} reports it genuinely has nothing.
 *
 * `non_field_errors` was handled in only 4 of the 15; routing it to `root` here fixes the
 * other 11, where a cross-field error previously vanished with no message at all.
 *
 * @param err      The thrown error.
 * @param t        i18next `t`.
 * @param setError The form's `setError` from `useForm()`.
 * @param fieldMap Backend field name → form field name. Backend fields absent from the map
 *                 fall through to `root`, so an unmapped error is surfaced, never swallowed.
 * @param rootField Where form-level messages go. Defaults to `"root"`. Override only for a
 *                 form that renders no root banner and deliberately pins the message to a
 *                 specific input (`ManageInvoiceDrawer` puts lifecycle 400s on `paid_date`).
 */
export function applyApiErrorToForm<TField extends string = string>(
  err: unknown,
  t: TranslateFn,
  setError: SetFormError<TField>,
  fieldMap: Record<string, TField>,
  rootField: TField | "root" = "root",
): ApplyFormErrorResult {
  const result = extractApiError(err, t);

  let appliedToField = false;
  const unmapped: string[] = [];

  for (const [backendField, message] of Object.entries(result.fieldErrors)) {
    const formField = fieldMap[backendField];
    if (formField) {
      setError(formField, { type: "server", message });
      appliedToField = true;
    } else {
      unmapped.push(message);
    }
  }

  // Form-level errors: anything the server sent that has no input to attach to. When some
  // fields DID match, the matched messages are already visible next to their inputs, so only
  // the genuinely unattached text is promoted to root — repeating a field message at form
  // level would read as two separate failures.
  const rootMessage = appliedToField
    ? unmapped.join(" ")
    : unmapped.join(" ") || result.message;

  let appliedToRoot = false;
  if (rootMessage) {
    setError(rootField, { type: "server", message: rootMessage });
    appliedToRoot = true;
  }

  return { ...result, appliedToField, appliedToRoot };
}

/**
 * The structured payload of a `CALENDAR_YEAR_NOT_COVERED` refusal (TASK-4933).
 *
 * `coveredYears` is what turns a dead end into an instruction: the dispatcher
 * asked for a year the checked-in table does not have, and the server already
 * knows which years it DOES have, so the message can name them instead of
 * inviting a guess-and-retry loop.
 */
export interface CalendarYearNotCoveredData {
  country: string | null;
  year: number | null;
  coveredYears: number[];
}

/**
 * Reads `extra_data.{country,year,covered_years}` off a thrown holiday-load
 * failure.
 *
 * The backend assigns `detail` DIRECTLY rather than through
 * `APIException.__init__` precisely so these arrive as real integers — DRF's
 * `_get_error_details` stringifies every leaf, which would have shipped
 * `["2026", "2027"]`. This reader therefore accepts numbers only and drops
 * anything else, rather than coercing: a string here means the backend
 * regressed, and rendering «2026, 2027» from coerced junk would hide that.
 *
 * Returns empty/`null` fields rather than throwing, so a caller can always
 * render the plain translated sentence when `extra_data` is absent.
 */
export function readCalendarYearNotCoveredData(
  err: unknown,
): CalendarYearNotCoveredData {
  const empty: CalendarYearNotCoveredData = {
    country: null,
    year: null,
    coveredYears: [],
  };
  const data = getResponseData(err);
  if (data == null || typeof data !== "object" || Array.isArray(data)) {
    return empty;
  }
  // The exception nests its payload one level down: `detail` is itself the
  // object `{detail, extra_data}`. Both shapes are probed so the reader keeps
  // working if the handler is ever flattened.
  const body = data as Record<string, unknown>;
  const nested =
    body.detail != null &&
    typeof body.detail === "object" &&
    !Array.isArray(body.detail)
      ? (body.detail as Record<string, unknown>)
      : null;
  const extraSource = (nested?.extra_data ?? body.extra_data) as unknown;
  if (
    extraSource == null ||
    typeof extraSource !== "object" ||
    Array.isArray(extraSource)
  ) {
    return empty;
  }
  const extra = extraSource as Record<string, unknown>;
  const years = Array.isArray(extra.covered_years)
    ? extra.covered_years.filter(
        (v): v is number => typeof v === "number" && Number.isFinite(v),
      )
    : [];
  return {
    country: typeof extra.country === "string" ? extra.country : null,
    year: readInt(extra, "year"),
    coveredYears: years,
  };
}

/**
 * Returns the `extra_data` object of a thrown API error, or `null` when it has none.
 *
 * Probes both body shapes the backend produces: flat `{detail, extra_data, code}`
 * and the nested `{detail: {detail, extra_data}}` an exception with a dict
 * `detail` can yield (see {@link readBlockingSchedules}). The caller narrows the
 * fields it needs — `extra_data` is untyped by contract.
 */
export function readApiErrorExtraData(
  err: unknown,
): Record<string, unknown> | null {
  const data = getResponseData(err);
  if (data == null || typeof data !== "object" || Array.isArray(data))
    return null;
  const body = data as Record<string, unknown>;
  const nested =
    body.detail != null &&
    typeof body.detail === "object" &&
    !Array.isArray(body.detail)
      ? (body.detail as Record<string, unknown>)
      : null;
  const extra = (nested?.extra_data ?? body.extra_data) as unknown;
  if (extra == null || typeof extra !== "object" || Array.isArray(extra)) {
    return null;
  }
  return extra as Record<string, unknown>;
}

/** One schedule blocking a route-template delete. */
export interface BlockingSchedule {
  id: number;
  name: string;
}

/**
 * Reads `extra_data.schedules` off a 409 `ROUTE_TEMPLATE_IN_USE` (BUG-557).
 *
 * `RouteSchedule.template` is PROTECT, so deleting a template that schedules
 * still stand on is refused. The backend names the blockers as
 * `[{id, name}]` — and assigns `detail` DIRECTLY rather than through
 * `APIException.__init__` precisely so the ids arrive as real integers, since
 * DRF's `_get_error_details` stringifies every leaf.
 *
 * Naming them is the whole point: a dispatcher looking at the template list
 * cannot see what a delete would take down, and «cannot delete» with nothing
 * named is not an actionable message. Without this the 409 fell through to a
 * generic toast.
 *
 * Returns `[]` rather than throwing when the block is absent, so the caller can
 * always fall back to the plain translated sentence.
 */
export function readBlockingSchedules(err: unknown): BlockingSchedule[] {
  const data = getResponseData(err);
  if (data == null || typeof data !== "object" || Array.isArray(data))
    return [];
  const body = data as Record<string, unknown>;
  // The exception nests its payload one level down: `detail` is itself the
  // object `{detail, extra_data}`. Both shapes are probed so the reader keeps
  // working if the handler is ever flattened.
  const nested =
    body.detail != null &&
    typeof body.detail === "object" &&
    !Array.isArray(body.detail)
      ? (body.detail as Record<string, unknown>)
      : null;
  const extraSource = (nested?.extra_data ?? body.extra_data) as unknown;
  if (
    extraSource == null ||
    typeof extraSource !== "object" ||
    Array.isArray(extraSource)
  ) {
    return [];
  }
  const schedules = (extraSource as Record<string, unknown>).schedules;
  if (!Array.isArray(schedules)) return [];
  return schedules.flatMap((row) => {
    if (row == null || typeof row !== "object" || Array.isArray(row)) return [];
    const entry = row as Record<string, unknown>;
    const id = entry.id;
    const name = entry.name;
    if (typeof id !== "number" || !Number.isFinite(id)) return [];
    return [{ id, name: typeof name === "string" ? name : "" }];
  });
}
